// Referrals Service
// File: src/services/referrals.service.ts

import { apiClient } from './api-client';
import { ApiError, ApiResponse } from '../dtos/common/api.dto';
import {
  ReferralDocument,
  ReferralData,
  CreateReferralRequest,
  CreateReferralResponse,
  ListReferralsResponse,
  GetDocumentResponse,
  ReferralIntakeRequest,
  ReferralIntakeResponse,
  PossibleDuplicatePatient,
  ConfirmReferralDocumentRequest
} from '../dtos/clinical/referral.dto';
import { PresignedUploadService, ticketRequestFor } from './uploads/presignedUpload';
import type { UploadTicket } from './uploads/presignedUpload';

// Re-export types for backward compatibility
export type {
  ConfirmReferralDocumentRequest,
  ReferralDocument,
  ReferralData,
  CreateReferralRequest,
  CreateReferralResponse,
  ListReferralsResponse,
  GetDocumentResponse,
  ReferralIntakeRequest,
  ReferralIntakeResponse,
  PossibleDuplicatePatient
};

/** Result of POST /patientmr/referrals/intake: created, or a non-blocking duplicate warning (REF-03). */
export type ReferralIntakeResult =
  | { kind: 'created'; patient: Record<string, unknown>; referral: ReferralData }
  | { kind: 'duplicate'; possibleDuplicates: PossibleDuplicatePatient[]; message: string };

/** documentType sent when confirming a referral document upload (R-206) */
export const REFERRAL_DOCUMENT_TYPE = 'REFERRAL';

const isDuplicateList = (value: unknown): value is PossibleDuplicatePatient[] =>
  Array.isArray(value) &&
  value.every((v) => v !== null && typeof v === 'object' && 'patientId' in (v as Record<string, unknown>));

/**
 * Referrals Service
 */
export const ReferralsService = {
  /**
   * Create a new referral
   */
  createReferral: async (
    patientId: string,
    referralData: CreateReferralRequest
  ): Promise<CreateReferralResponse> => {
    const response: ApiResponse<CreateReferralResponse> = await apiClient.post(
      `/patientmr/${patientId}/referrals`,
      referralData
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error?.message || 'Failed to create referral');
  },

  /**
   * Referral intake (REF-01): create a pending_admission patient shell and its referral in one
   * call. A 409 POSSIBLE_DUPLICATE is returned as `{ kind: 'duplicate' }` so the caller can offer
   * "Open existing chart" or resend with confirmNotDuplicate: true. Other errors throw.
   */
  intakeReferral: async (request: ReferralIntakeRequest): Promise<ReferralIntakeResult> => {
    try {
      const response: ApiResponse<ReferralIntakeResponse> = await apiClient.post(
        '/patientmr/referrals/intake',
        request
      );
      if (response.success && response.data) {
        return { kind: 'created', patient: response.data.patient, referral: response.data.referral };
      }
      throw new Error(response.error?.message || 'Failed to create the referral');
    } catch (error) {
      if (error instanceof ApiError && error.statusCode === 409 && error.domainCode === 'POSSIBLE_DUPLICATE') {
        const list = error.details?.possibleDuplicates;
        return {
          kind: 'duplicate',
          possibleDuplicates: isDuplicateList(list) ? list : [],
          message: error.message,
        };
      }
      throw error;
    }
  },

  /**
   * List all referrals for a patient
   */
  listReferrals: async (patientId: string): Promise<ListReferralsResponse> => {
    const response: ApiResponse<ListReferralsResponse> = await apiClient.get(
      `/patientmr/${patientId}/referrals`
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error?.message || 'Failed to list referrals');
  },

  /**
   * Update an existing referral
   */
  updateReferral: async (
    patientId: string,
    referralId: string,
    referralData: Partial<CreateReferralRequest>
  ): Promise<ReferralData> => {
    const response: ApiResponse<ReferralData> = await apiClient.put(
      `/patientmr/${patientId}/referrals/${referralId}`,
      referralData
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error?.message || 'Failed to update referral');
  },

  /**
   * Get a signed URL for downloading a document
   */
  getDocumentSignedUrl: async (
    patientId: string,
    referralId: string,
    documentId: string
  ): Promise<GetDocumentResponse> => {
    const response: ApiResponse<GetDocumentResponse> = await apiClient.get(
      `/patientmr/${patientId}/referrals/${referralId}/documents/${documentId}`
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error?.message || 'Failed to get document signed URL');
  },

  /** POST …/referrals/{referralId}/documents/upload-url → pre-signed S3 POST ticket (R-206) */
  getDocumentUploadTicket: async (
    patientId: string,
    referralId: string,
    file: File
  ): Promise<UploadTicket> => {
    const response: ApiResponse<UploadTicket> = await apiClient.post(
      `/patientmr/${patientId}/referrals/${referralId}/documents/upload-url`,
      ticketRequestFor(file)
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error?.message || 'Failed to get an upload link');
  },

  /** POST …/referrals/{referralId}/documents — confirm the uploaded object and add the document entry */
  confirmDocument: async (
    patientId: string,
    referralId: string,
    body: ConfirmReferralDocumentRequest
  ): Promise<ReferralDocument> => {
    const response: ApiResponse<{ document?: ReferralDocument } & ReferralDocument> = await apiClient.post(
      `/patientmr/${patientId}/referrals/${referralId}/documents`,
      body
    );

    if (response.success && response.data) {
      return response.data.document ?? response.data;
    }

    throw new Error(response.error?.message || 'Failed to save the document');
  },

  /**
   * Upload a referral document straight to S3 (ticket → S3 POST → confirm). The file never goes
   * through API Gateway; the server checks size and magic bytes on confirm.
   */
  uploadDocument: async (
    patientId: string,
    referralId: string,
    file: File,
    onProgress?: (pct: number) => void
  ): Promise<ReferralDocument> =>
    PresignedUploadService.uploadAndConfirm(
      file,
      () => ReferralsService.getDocumentUploadTicket(patientId, referralId, file),
      (key) =>
        ReferralsService.confirmDocument(patientId, referralId, {
          key,
          documentName: file.name,
          documentType: REFERRAL_DOCUMENT_TYPE,
        }),
      onProgress
    )
};

export default ReferralsService;
